import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, ArrowRight } from "lucide-react";

// Premium accessible fiche : centered modal (desktop) / bottom sheet (mobile).
export default function ServiceModal({ open, onClose, fiche, onDevis, onSecondary }) {
    const rm = useReducedMotion();
    const closeRef = useRef(null);
    const [desktop, setDesktop] = useState(true);

    useEffect(() => {
        setDesktop(window.innerWidth >= 640);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        const t = setTimeout(() => closeRef.current?.focus(), 60);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
            clearTimeout(t);
        };
    }, [open, onClose]);

    const panelInitial = rm
        ? { opacity: 0 }
        : desktop
        ? { opacity: 0, y: 24, scale: 0.96 }
        : { y: "100%" };
    const panelAnimate = rm ? { opacity: 1 } : desktop ? { opacity: 1, y: 0, scale: 1 } : { y: 0 };

    return createPortal(
        <AnimatePresence>
            {open && fiche && (
                <motion.div
                    className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    onClick={onClose}
                    aria-hidden={false}
                >
                    <div className="absolute inset-0 bg-ink/70 backdrop-blur-md" />
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="fiche-title"
                        className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[88vh] sm:max-h-[85vh] overflow-y-auto"
                        initial={panelInitial}
                        animate={panelAnimate}
                        exit={panelInitial}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="h-1.5 w-full french-stripe rounded-t-3xl sm:rounded-t-2xl" />
                        <button
                            ref={closeRef}
                            onClick={onClose}
                            aria-label="Fermer"
                            data-testid="fiche-close-btn"
                            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-mist text-navy hover:bg-navy hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-electric"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="sm:hidden flex justify-center pt-3">
                            <span className="w-10 h-1 rounded-full bg-line" />
                        </div>

                        <div className="p-7 sm:p-9">
                            <span className="uppercase text-[11px] tracking-[0.2em] font-bold text-brand-red font-body">Prestation</span>
                            <h3 id="fiche-title" className="text-2xl sm:text-3xl font-medium text-navy mt-2 mb-4 font-display leading-snug pr-10">
                                {fiche.label}
                            </h3>
                            <div className="h-px w-14 bg-brand-red mb-5" />
                            <p className="text-sm sm:text-base leading-relaxed text-slate-500 font-body">{fiche.desc}</p>

                            <p className="mt-6 mb-7 text-sm font-medium text-navy font-body flex items-start gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-electric mt-2 flex-shrink-0" />
                                Intervention adaptée à vos besoins, sur devis personnalisé
                            </p>

                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={onDevis}
                                    data-testid="fiche-devis-btn"
                                    className="group w-full inline-flex items-center justify-center gap-2 bg-electric text-white px-6 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-navy transition-colors duration-300"
                                >
                                    Demander un devis gratuit
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                {fiche.to && fiche.secondaryLabel && (
                                    <button
                                        onClick={() => onSecondary(fiche.to)}
                                        data-testid="fiche-secondary-btn"
                                        className="w-full inline-flex items-center justify-center gap-2 border border-navy text-navy px-6 py-3.5 text-sm uppercase tracking-wide font-semibold font-body hover:bg-navy hover:text-white transition-colors duration-300"
                                    >
                                        {fiche.secondaryLabel}
                                    </button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}
